import React from 'react';
import { DateTime } from 'luxon';
import { Badge, Box, Button, IconButton, Theme, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { InsertDriveFile, KeyboardArrowDown, PostAdd, Edit, Delete } from '@material-ui/icons';
import { TitularTooltip } from '@campfire/titular-tooltip';
import Icon from '@mdi/react';
import { iconList } from '../../../../../../common/icons/RoleIconList';
import { TooltipMenu } from '../../../../../../common/tooltip-menu';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';
import { displayTimeFunc, sessionAttendingCount } from './RosterTable';
import { useRosterTableStyles } from './RosterTableStyles';
import { MaxWarning } from './MaxWarning';
import { isOneOffSession } from '../one-off-session-utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    edit: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      paddingTop: '10px',
      justifyContent: 'center',
      '& p': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      textTransform: 'none',
    },
    oneOffBadge: {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      fontSize: '0.6rem',
      fontWeight: 600,
      padding: '3px 8px',
      borderRadius: '3px',
      textTransform: 'uppercase',
      display: 'inline-block',
      lineHeight: 1,
      textAlign: 'center' as const,
      position: 'absolute' as const,
      top: '-12px',
      left: '50%',
      transform: 'translateX(-50%)',
      whiteSpace: 'nowrap' as const,
    },
    sessionContainer: {
      position: 'relative' as const,
      display: 'inline-block',
      paddingTop: '14px',
    },
  })
);

type SessionHeaderProps = {
  session: any;
  draftRosters: any;
  hasSessionNotes?: boolean;
  showVolunteerRow: boolean;
  onClick?: () => void;
  printOnly?: boolean;
  onCustomiseSession?: (sessionId: string) => void;
  onEditOneOffSession?: (sessionId: string) => void;
  onDeleteOneOffSession?: (sessionId: string) => void;
  activityDate?: string;
};

export function SessionHeader({
  session,
  draftRosters,
  hasSessionNotes,
  showVolunteerRow,
  onClick,
  printOnly,
  onCustomiseSession,
  onEditOneOffSession,
  onDeleteOneOffSession,
  activityDate,
}: SessionHeaderProps) {
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const { theme, isMobile } = useCampfireTheme();
  const classes = useStyles();
  const rosterClasses = useRosterTableStyles({ isMobile });

  // Check if the activity date has passed
  const isActivityDatePassed = activityDate && DateTime.local().toISODate() > activityDate;
  return (
    <Box minWidth={printOnly ? '200px' : 'unset'}>
      <Box display='flex' alignItems='center' position='relative' justifyContent='center'>
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
          <Box display='flex' alignItems='center' justifyContent='center'>
            {session.isFlexible &&
            session.currentFlexibleInstances &&
            session.currentFlexibleInstances.length > 0 &&
            activityDate ? (
              (() => {
                // Find the flexible instance for the current activity date
                const flexibleInstance = session.currentFlexibleInstances.find((instance: any) => {
                  const instanceDate =
                    typeof instance.sessionDate === 'string'
                      ? instance.sessionDate
                      : DateTime.fromJSDate(instance.sessionDate).toISODate();
                  return instanceDate === activityDate;
                });

                if (flexibleInstance?.name) {
                  return (
                    <Box display='flex' flexDirection='column' alignItems='center'>
                      <Typography
                        variant='caption'
                        style={{ fontSize: '0.7rem', color: theme.color.grey.neutralBrand500, marginBottom: '2px' }}
                      >
                        {session.name}
                      </Typography>
                      <Box className={classes.sessionContainer} style={{ paddingTop: '0px' }}>
                        {isOneOffSession(session) && <Box className={classes.oneOffBadge}>One-Off</Box>}
                        <span>{flexibleInstance.name}</span>
                      </Box>
                    </Box>
                  );
                }
                return (
                  <Box className={classes.sessionContainer}>
                    {isOneOffSession(session) && <Box className={classes.oneOffBadge}>One-Off</Box>}
                    <span>{session.name}</span>
                  </Box>
                );
              })()
            ) : (
              <Box className={classes.sessionContainer}>
                {isOneOffSession(session) && <Box className={classes.oneOffBadge}>One-Off</Box>}
                <span>{session.name}</span>
              </Box>
            )}
          </Box>
          <Typography classes={{ root: rosterClasses.rowTime }}>{displayTimeFunc(session, activityDate)}</Typography>
          {session.requiredRoles && session.requiredRoles.length > 0 && (
            <Box display='flex' alignItems='center' justifyContent='center' mt={0.5} style={{ gap: 4 }}>
              {session.requiredRoles.slice(0, 3).map((role: any) => (
                <TitularTooltip key={role.activityRoleId} description={role.name}>
                  <Box style={{ display: 'flex', alignItems: 'center' }}>
                    {role.icon && iconList[role.icon] ? (
                      <Icon path={iconList[role.icon]} size={0.55} color={theme.color.grey.neutral400} />
                    ) : (
                      <Typography style={{ fontSize: 10, color: theme.color.grey.neutral400 }}>
                        {role.name.charAt(0)}
                      </Typography>
                    )}
                  </Box>
                </TitularTooltip>
              ))}
              {session.requiredRoles.length > 3 && (
                <TitularTooltip
                  description={session.requiredRoles
                    .slice(3)
                    .map((r: any) => r.name)
                    .join(', ')}
                >
                  <Typography style={{ fontSize: 10, color: theme.color.grey.neutral300, fontWeight: 600 }}>
                    +{session.requiredRoles.length - 3}
                  </Typography>
                </TitularTooltip>
              )}
            </Box>
          )}
        </Box>
        {!printOnly && !isActivityDatePassed && (
          <TooltipMenu
            open={tooltipOpen}
            onClose={() => {
              setTooltipOpen(false);
            }}
            title={
              <Box display='flex' flexDirection='column' alignItems='flex-start'>
                <Button startIcon={<PostAdd />} className={classes.edit} onClick={onClick}>
                  {hasSessionNotes ? 'Edit' : 'Add'} session notes
                </Button>
                {session.isFlexible && onCustomiseSession && (
                  <Button
                    startIcon={<Edit />}
                    className={classes.edit}
                    onClick={() => onCustomiseSession(session.sessionId)}
                  >
                    Customise Session
                  </Button>
                )}
                {isOneOffSession(session) && onEditOneOffSession && (
                  <Button
                    startIcon={<Edit />}
                    className={classes.edit}
                    onClick={() => {
                      setTooltipOpen(false);
                      onEditOneOffSession(session.sessionId);
                    }}
                  >
                    Edit session
                  </Button>
                )}
                {isOneOffSession(session) && onDeleteOneOffSession && (
                  <Button
                    startIcon={<Delete />}
                    className={classes.edit}
                    onClick={() => {
                      setTooltipOpen(false);
                      onDeleteOneOffSession(session.sessionId);
                    }}
                  >
                    Delete session
                  </Button>
                )}
              </Box>
            }
          >
            <IconButton onClick={() => setTooltipOpen(true)} size='small' style={{ marginLeft: '1rem' }}>
              <KeyboardArrowDown />
            </IconButton>
          </TooltipMenu>
        )}
      </Box>
      <Box paddingTop={'15px'}>
        {hasSessionNotes && (
          <Badge
            onClick={onClick}
            style={{
              backgroundColor: theme.color.grey.neutral200,
              color: '#ffffff',
              padding: '6px',
              borderRadius: '3px',
              height: 'fit-content',
              marginTop: '7px',
              cursor: 'pointer',
            }}
          >
            <InsertDriveFile style={{ fontSize: '16px' }} />
            <Typography style={{ fontSize: '12px', paddingLeft: '4px', fontWeight: 500, paddingRight: '2px' }}>
              Note Added
            </Typography>
          </Badge>
        )}
        {showVolunteerRow && (
          <Box>
            {session.isMaxed ? (
              <MaxWarning />
            ) : (
              sessionAttendingCount(draftRosters)(session) > 0 && (
                <Typography className={rosterClasses.rowVolunteer} style={{ marginTop: '10px' }}>
                  Need <strong>{sessionAttendingCount(draftRosters)(session)}</strong> volunteers
                </Typography>
              )
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
